import { useState, useEffect } from 'react'
import moment from 'moment'
import Cookies from 'universal-cookie'

import RequesterConfiguration, {
  requesterConfigurationMethods
} from 'core/requesters/RequesterConfiguration'
import Hydrator, { hydratorMethods } from 'core/hydrator'
import guid from 'core/utils/enums/guid'
import { initOrejime } from 'core/utils/cookies/config'

const useGetConfig = pathname => {
  const [hasError, setHasError] = useState(false)
  const [config, setConfig] = useState(null)
  const [tmpConfig, setTmpConfig] = useState(null)
  const [token, setToken] = useState(null)
  const [releaseId, setReleaseId] = useState(null)
  const [silentSignIn, setSilentSignIn] = useState(false)
  const [loading, setloading] = useState(false)
  const [lang, setLang] = useState(null)
  const [idReferential, setIdReferential] = useState(null)
  const [currentPrivacyPolicy, setPrivacyPolicy] = useState(null)
  const [idCampaign, setIdCampaign] = useState(null)
  const [applicationOptions, setApplicationOptions] = useState(null)
  const [currentPricingPolicy, setCurrentPricingPolicy] = useState(null)

  const getConfigCampaign = params => {
    if (silentSignIn) {
      RequesterConfiguration.get(
        requesterConfigurationMethods.GET_SILENT_CONFIG,
        params,
        null,
        false
      )
        .then(datas => {
          const { opportunitySettings, pricingPolicy } = datas
          setTmpConfig({
            ...config,
            privacyPolicy: currentPrivacyPolicy,
            currentPricingPolicy: pricingPolicy,
            opportunitySettings,
            applicationOptions
          })
          setCurrentPricingPolicy(pricingPolicy)
        })
        .catch(() => {
          setHasError(true)
          setloading(false)
        })
    } else {
      RequesterConfiguration.get(requesterConfigurationMethods.GET_CONFIG, params, null, false)
        .then(datas => {
          const { campaign, providerConfiguration, opportunitySettings, pricingPolicy } = datas

          const extentedCampaign = { ...campaign, lang, idReferential, uid: idCampaign }

          const extendedProviderConfiguration = {
            ...providerConfiguration
          }

          initOrejime(
            campaign.privacyPolicyUrl,
            campaign.googleAnalyticsId,
            campaign.cookiesConfiguration
          )

          setTmpConfig({
            campaign: Hydrator.hydrate(hydratorMethods.HYDRATE_CAMPAIGN, extentedCampaign),
            providerConfiguration: extendedProviderConfiguration,
            privacyPolicy: currentPrivacyPolicy,
            currentPricingPolicy: pricingPolicy,
            opportunitySettings,
            applicationOptions
          })
          setCurrentPricingPolicy(pricingPolicy)
        })
        .catch(() => {
          setHasError(true)
          setloading(false)
        })
    }
  }

  const getAutomaticValidationSettings = params => {
    RequesterConfiguration.get(
      requesterConfigurationMethods.GET_AUTOMATIC_VALIDATION_SETTINGS,
      params
    )
      .then(datas => {
        setConfig({
          ...tmpConfig,
          automaticValidationSetting: datas.automaticValidation
        })
      })
      .catch(() => {
        setHasError(true)
      })
      .finally(setloading(false))
  }

  const getCampaignContext = params => {
    RequesterConfiguration.get(requesterConfigurationMethods.GET_CONTEXT, params, null)
      .then(datas => {
        const { referentialId, culture, entity, pointOfSale, privacyPolicy, campaign } = datas
        const { entityGroup } = entity
        const { options } = entity.applications.find(x => x.guid === guid.OPENFLEX)

        setLang(culture)
        setIdReferential(referentialId)
        setIdCampaign(campaign.uid)
        setPrivacyPolicy({ ...privacyPolicy })
        setApplicationOptions(options)

        const bearer = {
          jwt: token,
          date: moment(),
          pathname,
          entityGroupId: entityGroup.id,
          originEntityGroupId: campaign.entityGroupId,
          entityId: entity.id,
          pointOfSaleId: pointOfSale.id,
          campaignId: campaign.id,
          privacyPolicy: privacyPolicy.id,
          applicationOptions: options
        }

        localStorage.setItem('bearer', JSON.stringify(bearer))
      })
      .catch(() => {
        setHasError(true)
        setloading(false)
      })
  }

  const getConfig = async params => {
    setHasError(false)
    setloading(true)
    setSilentSignIn(!!params.pointOfSaleId)

    return RequesterConfiguration.get(requesterConfigurationMethods.GET_TOKEN, params)
      .then(datas => {
        const bearer = {
          jwt: datas.token
        }

        const context = JSON.parse(atob(datas.token.split('.')[1]))
        if (context.options[guid.GLOBALDEALER]) {
          const version = context.options[guid.GLOBALDEALER].v
          const cookies = new Cookies()
          if (window.location.hostname.indexOf('localhost') !== -1) {
            cookies.set('v', version, { path: '/', secure: false, sameSite: 'lax' })
          } else {
            cookies.set('v', version, { path: '/', secure: true, sameSite: 'none' })
          }
          setReleaseId(version)
        }

        localStorage.setItem('bearer', JSON.stringify(bearer))
        setToken(datas.token)
      })
      .catch(() => {
        setHasError(true)
        setloading(false)
      })
  }

  useEffect(() => {
    const runIt = () => {
      getConfigCampaign()
    }

    if (lang && idReferential && idCampaign && currentPrivacyPolicy && applicationOptions) runIt()
  }, [applicationOptions])

  useEffect(() => {
    const runIt = () => {
      getAutomaticValidationSettings({
        id: currentPricingPolicy.id,
        automaticValidationId: currentPricingPolicy.automaticValidationId
      })
    }

    if (currentPricingPolicy) runIt()
  }, [currentPricingPolicy])

  useEffect(() => {
    const runIt = () => {
      getCampaignContext()
    }
    if (token) runIt()
  }, [token])

  return {
    getConfig,
    config,
    hasError,
    loading,
    token,
    releaseId
  }
}

export default useGetConfig
